// ./src/components/features/ScheduleFiltersForm/ScheduleFiltersForm.tsx
import React from "react";
import { Grid, TextInput, Stack, Text, NumberInput } from "@mantine/core";
import { RoomSelector, RoomTypeSelector } from "../Rooms";
import { VenueSelector } from "../Venues";
import { ScheduleTypeSelector } from "../DisplayConfigurations";
import { CategorSelector } from "../Categories";

export interface ScheduleFiltersFormValue {
  scheduleType: string | null | undefined;
  venueId?: string | null | undefined;
  roomId?: string | null | undefined;
  floor?: string | null | undefined;
  roomTypeId?: string | null | undefined;
  roomTag?: string | null | undefined;
  eventTag?: string | null | undefined;
  roomCategoryId?: string | null | undefined;
  eventCategoryId?: string | null | undefined;
  showPastHours?: number | null | undefined;
  showUpcomingHours?: number | null | undefined;
}

export interface ScheduleFiltersFormProps {
  /** Current schedule filters value */
  value: ScheduleFiltersFormValue | null | undefined;

  /** Callback when filters change */
  onChange: (value: ScheduleFiltersFormValue) => void;

  /** Venue ID context (optional, can be overridden in value.venueId) */
  venueId?: string | null;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ScheduleFiltersForm: React.FC<ScheduleFiltersFormProps> = ({
  value,
  onChange,
  venueId,
  error,
  disabled = false,
  size = "md",
}) => {
  const currentValue = value || {
    scheduleType: null,
    venueId: null,
    roomId: null,
    floor: null,
    roomTypeId: null,
    roomTag: null,
    eventTag: null,
    showPastHours: null,
    showUpcomingHours: null,
    roomCategoryId: null,
    eventCategoryId: null,
  };

  const handleFieldChange = (
    field: keyof ScheduleFiltersFormValue,
    fieldValue: string | number | null | string[] | undefined
  ) => {
    onChange({
      ...currentValue,
      [field]: Array.isArray(fieldValue) ? fieldValue[0] : fieldValue,
    });
  };

  const handleVenueChange = (venueIdValue: string | string[] | null) => {
    const newVenueId = Array.isArray(venueIdValue)
      ? venueIdValue[0]
      : venueIdValue;
    onChange({
      ...currentValue,
      venueId: newVenueId,
      roomId: null, // Clear room when venue changes
    });
  };

  const handleRoomChange = (roomId: string | string[] | null) => {
    handleFieldChange("roomId", roomId);
  };

  const handleRoomTypeChange = (roomTypeId: string | null) => {
    handleFieldChange("roomTypeId", roomTypeId);
  };

  const handleScheduleTypeChange = (
    scheduleType: string | null | undefined
  ) => {
    handleFieldChange("scheduleType", scheduleType);
  };

  // Use venueId from value first, then fall back to prop
  const effectiveVenueId = currentValue.venueId || venueId;

  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={12}>
          <ScheduleTypeSelector
            label="Schedule Type"
            placeholder="Select schedule type"
            value={currentValue.scheduleType}
            onChange={(val) => handleScheduleTypeChange(val)}
            disabled={disabled}
            size={size}
            required
          />
        </Grid.Col>
      </Grid>

      <Text size="sm" fw={500} mt="xs">
        Filters (Optional - All use AND logic)
      </Text>

      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <VenueSelector
            label="Venue"
            value={currentValue.venueId || null}
            onChange={handleVenueChange}
            placeholder="Select venue (optional)"
            disabled={disabled}
            size={size}
            clearable
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <RoomSelector
            label="Room"
            venueId={effectiveVenueId || ""}
            value={currentValue.roomId || null}
            onChange={handleRoomChange}
            placeholder="Select room (optional)"
            disabled={disabled || !effectiveVenueId}
            size={size}
            clearable
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Floor"
            placeholder="Enter floor (e.g., 1, 2, Ground)"
            value={currentValue.floor || ""}
            onChange={(e) => handleFieldChange("floor", e.target.value || null)}
            disabled={disabled}
            size={size}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <RoomTypeSelector
            label="Room Type"
            value={currentValue.roomTypeId || null}
            onChange={handleRoomTypeChange}
            placeholder="Select room type (optional)"
            disabled={disabled}
            size={size}
            clearable
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CategorSelector
            label="Room Category"
            placeholder="Select room category"
            value={currentValue.roomCategoryId}
            onChange={(categoryId) =>
              handleFieldChange("roomCategoryId", categoryId)
            }
            disabled={disabled}
            size={size}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CategorSelector
            label="Event Category"
            placeholder="Select event category"
            value={currentValue.eventCategoryId}
            onChange={(categoryId) =>
              handleFieldChange("eventCategoryId", categoryId)
            }
            disabled={disabled}
            size={size}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <NumberInput
            label="Show past events for (hours)"
            value={currentValue.showPastHours || ""}
            onChange={(value) => handleFieldChange("showPastHours", value)}
            //error={getDisplayError("showPastHours")}
            min={0}
            max={24}
            step={0.1}
            allowNegative={false}
            allowDecimal={true}
            disabled={disabled}
            size={size}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <NumberInput
            label="Show upcoming events within (hr)"
            value={currentValue.showUpcomingHours || ""}
            onChange={(value) => handleFieldChange("showUpcomingHours", value)}
            //error={getDisplayError("showUpcomingHours")}
            min={0}
            max={24}
            step={0.1}
            allowNegative={false}
            allowDecimal={true}
            disabled={disabled}
            size={size}
          />
        </Grid.Col>
      </Grid>

      {error && (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
};
